// Client-side product filter, search, sorting and "load more" for the shop
// archive.
//
// The whole archive is rendered into the page (see the loop_shop_per_page
// filter in src/pages/shop/main.php), so none of this ever touches the
// network. Every product <li> already carries its taxonomy terms as classes --
// `product_cat-<slug>` and `product_brand-<slug>` -- because WooCommerce wraps
// products in get_post_class() output. The price rides along as `js-price-490`
// (whole cents), added by the same hook.
//
// The search field is the custom/product-search block. It sits in the header
// template part and therefore also exists on pages without a grid -- there
// nothing below runs and the form stays an ordinary GET form pointing at the
// shop, which lands here as the `suche` parameter.
import { trackHeaderHeight } from './header-offset';

const GRID_SELECTOR    = 'ul.wc-block-product-template';
const ITEM_SELECTOR    = 'li.wc-block-product';
const PRICE_PREFIX     = 'js-price-';
const DEFAULT_SORT     = 'default';
const DEFAULT_PAGESIZE = 16;
const SEARCH_PARAM     = 'suche';

// Taxonomy -> query parameter. Anything not listed falls back to its taxonomy
// name, so a third dimension needs no change here.
const URL_PARAMS: Record<string, string> = {
  product_cat   : 'kategorie',
  product_brand : 'marke',
};

interface Product {
  el     : HTMLElement;
  terms  : Map<string, string>;   // taxonomy -> term slug
  price  : number;                // in whole cents
  name   : string;
  search : string;                // normalised name plus category and brand
  order  : number;                // position as rendered by WooCommerce
}

interface Dimension {
  taxonomy : string;
  param    : string;
  inputs   : HTMLInputElement[];
  counts   : Map<string, HTMLElement>;
  items    : Map<string, HTMLElement>;
  labels   : Map<string, string>;
}

interface State {
  selected : Map<string, Set<string>>;   // taxonomy -> selected slugs
  search   : string;                     // as typed, for URL and chip
  tokens   : string[];                   // normalised words of the search
  sort     : string;
  shown    : number;
}

interface Elements {
  grid     : HTMLElement;
  result   : HTMLElement | null;
  chips    : HTMLElement | null;
  reset    : HTMLElement | null;
  empty    : HTMLElement | null;
  sort     : HTMLSelectElement | null;
  more     : HTMLElement | null;
  moreBtn  : HTMLElement | null;
  badge    : HTMLElement | null;   // Zaehler im mobilen Filter-Knopf
  apply    : HTMLElement | null;   // "N Ergebnisse anzeigen" im Drawer
  search   : HTMLInputElement[];   // custom/product-search, meist im Seitenkopf
  forms    : HTMLFormElement[];
}

function init(): void {
  trackHeaderHeight();

  const grid = document.querySelector<HTMLElement>(GRID_SELECTOR);
  if (grid === null) return;

  const filterRoots = Array.from(
    document.querySelectorAll<HTMLElement>('.c-product-filter[data-taxonomy]')
  );

  const dimensions = filterRoots.map(readDimension);
  const products   = readProducts(grid, dimensions);

  // Die mobile Ansicht zeigt die Filter in einem Drawer. Dessen Rahmen wird
  // hier gebaut, nicht serverseitig: ohne JavaScript liesse er sich nicht
  // oeffnen, und die Sidebar bleibt dann schlicht ein normaler Abschnitt.
  buildDrawer(filterRoots);

  const elements = readElements(grid);
  const pageSize = readPageSize(elements.more);

  const state: State = {
    selected : new Map(dimensions.map(d => [d.taxonomy, new Set<string>()])),
    search   : '',
    tokens   : [],
    sort     : DEFAULT_SORT,
    shown    : pageSize,
  };

  const draw = () => render(state, dimensions, products, elements, pageSize);

  // Switches off the server-side "first page only" CSS from
  // product-load-more/render.php -- from here on the `hidden` attribute rules.
  document.documentElement.classList.add('c-products-ready');

  readUrl(state, dimensions, pageSize);
  syncControls(state, dimensions, elements);
  draw();

  // ---- Events ----
  dimensions.forEach(dimension => {
    dimension.inputs.forEach(input => {
      input.addEventListener('change', () => {
        const selected = state.selected.get(dimension.taxonomy);
        if (selected === undefined) return;

        if (input.checked) selected.add(input.value);
        else               selected.delete(input.value);

        state.shown = pageSize;
        draw();
        pushUrl(state, dimensions);
      });
    });
  });

  // Typing narrows the grid straight away, exactly like ticking a checkbox.
  // The URL is only replaced, not pushed: one history entry per keystroke
  // would make the back button unusable.
  elements.search.forEach(input => {
    input.addEventListener('input', () => {
      setSearch(state, input.value);
      state.shown = pageSize;

      elements.search.forEach(other => {
        if (other !== input) other.value = state.search;
      });

      draw();
      pushUrl(state, dimensions, true);
    });
  });

  // Submitting would reload the page for a result that is already on screen.
  elements.forms.forEach(form => {
    form.addEventListener('submit', event => event.preventDefault());
  });

  elements.sort?.addEventListener('change', () => {
    state.sort  = elements.sort?.value ?? DEFAULT_SORT;
    state.shown = pageSize;
    draw();
    pushUrl(state, dimensions);
  });

  elements.reset?.addEventListener('click', () => {
    state.selected.forEach(selected => selected.clear());
    setSearch(state, '');
    state.shown = pageSize;
    syncControls(state, dimensions, elements);
    draw();
    pushUrl(state, dimensions);
  });

  // Revealing more is not a navigation step -- no history entry for it.
  elements.moreBtn?.addEventListener('click', () => {
    state.shown += pageSize;
    draw();
  });

  window.addEventListener('popstate', () => {
    readUrl(state, dimensions, pageSize);
    syncControls(state, dimensions, elements);
    draw();
  });

  bindDrawer();
}




// ---- Mobile drawer ----

const OPEN_CLASS = 'c-filter-open';

/**
 * Turns the filter sidebar into a bottom sheet on small screens: a trigger in
 * the toolbar, a backdrop, and a header and footer inside the panel. All of it
 * is inert on desktop, where the CSS keeps it hidden.
 */
function buildDrawer(filterRoots: HTMLElement[]): void {
  const first = filterRoots[0];
  if (first === undefined) return;

  const panel = first.closest<HTMLElement>('.c-shop-sidebar') ?? first.parentElement;
  const bar   = document.querySelector<HTMLElement>('.c-product-toolbar__bar');
  if (panel === null || bar === null) return;

  // --- Trigger in der Toolbar ---
  const toggle = document.createElement('button');
  const badge  = document.createElement('span');

  toggle.type      = 'button';
  toggle.className = 'c-product-toolbar__filter-toggle';
  toggle.append('Filter');

  badge.className = 'c-product-toolbar__filter-badge';
  badge.hidden    = true;
  toggle.appendChild(badge);
  bar.appendChild(toggle);

  // --- Kopf des Drawers ---
  const head  = document.createElement('div');
  const title = document.createElement('p');
  const close = document.createElement('button');

  head.className  = 'c-filter-drawer-head';
  title.className = 'c-filter-drawer-title';
  title.textContent = 'Filter';

  close.type        = 'button';
  close.className   = 'c-filter-drawer-close';
  close.textContent = '×';
  close.setAttribute('aria-label', 'Filter schliessen');

  head.appendChild(title);
  head.appendChild(close);
  panel.insertBefore(head, panel.firstChild);

  // --- Fuss des Drawers ---
  const foot  = document.createElement('div');
  const apply = document.createElement('button');

  foot.className  = 'c-filter-drawer-foot';
  apply.type      = 'button';
  apply.className = 'c-filter-drawer-apply';

  foot.appendChild(apply);
  panel.appendChild(foot);

  // --- Backdrop ---
  const backdrop = document.createElement('div');
  backdrop.className = 'c-filter-backdrop';
  document.body.appendChild(backdrop);

  // Erst dieses Flag schaltet das Drawer-CSS scharf. Ohne den Rahmen -- kein
  // JavaScript, alte main.js, ein Fehler weiter oben -- bleibt die Sidebar
  // auch auf dem Handy ein normaler Abschnitt und damit bedienbar.
  document.documentElement.classList.add('c-drawer-ready');
}

function bindDrawer(): void {
  const root     = document.documentElement;
  const toggle   = document.querySelector<HTMLElement>('.c-product-toolbar__filter-toggle');
  const close    = document.querySelector<HTMLElement>('.c-filter-drawer-close');
  const apply    = document.querySelector<HTMLElement>('.c-filter-drawer-apply');
  const backdrop = document.querySelector<HTMLElement>('.c-filter-backdrop');
  const panel    = document.querySelector<HTMLElement>('.c-shop-sidebar');

  const open  = () => root.classList.add(OPEN_CLASS);
  const shut  = () => root.classList.remove(OPEN_CLASS);

  toggle  ?.addEventListener('click', open);
  close   ?.addEventListener('click', shut);
  apply   ?.addEventListener('click', shut);
  backdrop?.addEventListener('click', shut);

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') shut();
  });

  if (panel !== null) bindSwipe(panel, shut);
}


// Ab hier gilt die Bewegung als Geste und nicht mehr als Tippen.
const DRAG_THRESHOLD = 8;
// Zurueckgelegter Weg, ab dem der Drawer beim Loslassen zugeht.
const CLOSE_DISTANCE = 90;
// px/ms: ein kurzer, schneller Wisch reicht auch ohne die volle Strecke.
const CLOSE_VELOCITY = 0.5;
const FLICK_DISTANCE = 24;

/**
 * Wischen nach unten schliesst den Drawer -- auf Touchgeraeten die erwartete
 * Geste neben dem Schliessen-Knopf. Das Panel haengt dabei am Finger und
 * federt zurueck, wenn der Weg zu kurz war.
 *
 * Die Geste beginnt nur am oberen Anschlag der Filterliste oder mit dem Finger
 * auf dem Kopf mit dem Griff -- mitten in einer gescrollten Liste gehoert die
 * vertikale Bewegung dem Scrollen.
 */
function bindSwipe(panel: HTMLElement, shut: () => void): void {
  const head = panel.querySelector<HTMLElement>('.c-filter-drawer-head');

  let startX   = 0;
  let startY   = 0;
  let startAt  = 0;      // Zeitstempel fuer die Geschwindigkeit
  let offset   = 0;      // aktuelle Verschiebung in px
  let active   = false;  // Finger liegt auf, Richtung noch offen
  let dragging = false;  // Richtung entschieden: das Panel folgt dem Finger

  /** Beendet die Geste: entweder zu, oder zurueck in die offene Lage. */
  const settle = (close: boolean): void => {
    active   = false;
    dragging = false;
    offset   = 0;

    // Beides im selben Frame: der Browser rechnet die Transition dann von der
    // Fingerposition aus -- entweder auf translateY(100%) oder zurueck auf 0.
    panel.classList.remove('is-dragging');
    panel.style.removeProperty('transform');

    if (close) shut();
  };

  panel.addEventListener('touchstart', event => {
    if (!document.documentElement.classList.contains(OPEN_CLASS)) return;
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    if (touch === undefined) return;

    const onHead = head !== null && touch.target instanceof Node && head.contains(touch.target);
    if (!onHead && panel.scrollTop > 0) return;

    startX   = touch.clientX;
    startY   = touch.clientY;
    startAt  = event.timeStamp;
    active   = true;
    dragging = false;
  }, { passive: true });

  panel.addEventListener('touchmove', event => {
    if (!active) return;

    const touch = event.touches[0];
    if (touch === undefined) return;

    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (!dragging) {
      // Quer oder nach oben: das ist Scrollen, und danach kein Zuziehen mehr.
      if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
        if (Math.max(Math.abs(dx), -dy) > DRAG_THRESHOLD) active = false;
        return;
      }
      if (dy < DRAG_THRESHOLD) return;

      dragging = true;
      panel.classList.add('is-dragging');
    }

    offset = Math.max(0, dy);
    panel.style.transform = `translateY(${offset}px)`;

    // Sonst scrollt der Browser die Liste unter dem Finger mit.
    if (event.cancelable) event.preventDefault();
  }, { passive: false });

  panel.addEventListener('touchend', event => {
    if (!dragging) {
      active = false;
      return;
    }

    const elapsed  = Math.max(1, event.timeStamp - startAt);
    const velocity = offset / elapsed;

    settle(offset > CLOSE_DISTANCE || (velocity > CLOSE_VELOCITY && offset > FLICK_DISTANCE));
  });

  panel.addEventListener('touchcancel', () => settle(false));
}


// ---- Reading the page ----

function readDimension(root: HTMLElement): Dimension {
  const taxonomy = root.dataset['taxonomy'] ?? '';
  const inputs   = Array.from(
    root.querySelectorAll<HTMLInputElement>('.c-product-filter__checkbox')
  );

  const counts = new Map<string, HTMLElement>();
  const items  = new Map<string, HTMLElement>();
  const labels = new Map<string, string>();

  inputs.forEach(input => {
    const item  = input.closest<HTMLElement>('.c-product-filter__item');
    const count = root.querySelector<HTMLElement>(
      `.c-product-filter__count[data-slug="${CSS.escape(input.value)}"]`
    );
    const label = input
      .closest('.c-product-filter__option')
      ?.querySelector<HTMLElement>('.c-product-filter__label');

    if (item  !== null && item  !== undefined) items .set(input.value, item);
    if (count !== null)                        counts.set(input.value, count);
    labels.set(input.value, label?.textContent?.trim() ?? input.value);
  });

  return {
    taxonomy,
    param : URL_PARAMS[taxonomy] ?? taxonomy,
    inputs,
    counts,
    items,
    labels,
  };
}

function readProducts(grid: HTMLElement, dimensions: Dimension[]): Product[] {
  const items = Array.from(grid.querySelectorAll<HTMLElement>(ITEM_SELECTOR));

  return items.map((el, order) => {
    const classes = Array.from(el.classList);
    const terms   = new Map<string, string>();

    dimensions.forEach(dimension => {
      const prefix = `${dimension.taxonomy}-`;
      const match  = classes.find(name => name.startsWith(prefix));
      if (match !== undefined) terms.set(dimension.taxonomy, match.slice(prefix.length));
    });

    const priceClass = classes.find(name => name.startsWith(PRICE_PREFIX));
    const price      = priceClass === undefined
      ? 0
      : parseInt(priceClass.slice(PRICE_PREFIX.length), 10) || 0;

    const title = el.querySelector<HTMLElement>('.wp-block-post-title');
    const name  = title?.textContent?.trim() ?? '';

    // The search reads the card, not a data attribute: category and brand are
    // rendered as <div class="wp-block-post-terms">, so "bohnen" finds every
    // product of that category without any extra markup.
    const terminology = Array.from(el.querySelectorAll<HTMLElement>('.wp-block-post-terms'))
      .map(node => node.textContent ?? '')
      .join(' ');

    return {
      el,
      terms,
      price,
      name,
      search : normalize(`${name} ${terminology}`),
      order,
    };
  });
}

function readElements(grid: HTMLElement): Elements {
  const more = document.querySelector<HTMLElement>('.c-product-load-more');

  return {
    grid,
    result  : document.querySelector<HTMLElement>('.c-product-toolbar__result'),
    chips   : document.querySelector<HTMLElement>('.c-product-toolbar__chips'),
    reset   : document.querySelector<HTMLElement>('.c-product-toolbar__reset'),
    empty   : document.querySelector<HTMLElement>('.c-product-toolbar__empty'),
    sort    : document.querySelector<HTMLSelectElement>('.c-product-toolbar__sort'),
    more,
    moreBtn : more?.querySelector<HTMLElement>('.c-product-load-more__button') ?? null,
    badge   : document.querySelector<HTMLElement>('.c-product-toolbar__filter-badge'),
    apply   : document.querySelector<HTMLElement>('.c-filter-drawer-apply'),
    search  : Array.from(document.querySelectorAll<HTMLInputElement>('.c-product-search__input')),
    forms   : Array.from(document.querySelectorAll<HTMLFormElement>('form.c-product-search')),
  };
}


// ---- Search ----

/**
 * Lowercase and without diacritics, so "cafe" also finds "Caffè" and the
 * comparison never depends on how a name happens to be spelled.
 */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * Keeps the typed term and the words it is matched by in step. Splitting into
 * words makes the order irrelevant -- "lollo bohnen" finds the same product as
 * "bohnen lollo".
 */
function setSearch(state: State, value: string): void {
  state.search = value.trim();
  state.tokens = normalize(value).split(/\s+/).filter(token => token !== '');
}

function readPageSize(more: HTMLElement | null): number {
  const raw    = more?.dataset['pageSize'] ?? '';
  const parsed = parseInt(raw, 10);
  return isNaN(parsed) || parsed < 1 ? DEFAULT_PAGESIZE : parsed;
}


// ---- URL state ----

function readUrl(state: State, dimensions: Dimension[], pageSize: number): void {
  const params = new URLSearchParams(window.location.search);

  dimensions.forEach(dimension => {
    const selected = state.selected.get(dimension.taxonomy);
    if (selected === undefined) return;

    selected.clear();
    const raw = params.get(dimension.param);
    if (raw === null || raw === '') return;

    raw.split(',')
      .map(slug => slug.trim())
      .filter(slug => dimension.labels.has(slug))
      .forEach(slug => selected.add(slug));
  });

  setSearch(state, params.get(SEARCH_PARAM) ?? '');

  state.sort  = params.get('sort') ?? DEFAULT_SORT;
  state.shown = pageSize;
}

/**
 * `replace` keeps the current history entry instead of adding one -- what
 * typing in the search field needs, where every keystroke gets here.
 */
function pushUrl(state: State, dimensions: Dimension[], replace = false): void {
  const params = new URLSearchParams(window.location.search);

  dimensions.forEach(dimension => {
    const selected = state.selected.get(dimension.taxonomy);
    if (selected === undefined || selected.size === 0) params.delete(dimension.param);
    else params.set(dimension.param, Array.from(selected).join(','));
  });

  if (state.search === '') params.delete(SEARCH_PARAM);
  else params.set(SEARCH_PARAM, state.search);

  if (state.sort === DEFAULT_SORT) params.delete('sort');
  else params.set('sort', state.sort);

  const query = params.toString();
  const url   = query === '' ? window.location.pathname : `${window.location.pathname}?${query}`;

  if (replace) window.history.replaceState(null, '', url);
  else         window.history.pushState(null, '', url);
}

/** Mirrors the state back into the checkboxes, the search field and the sort dropdown. */
function syncControls(state: State, dimensions: Dimension[], elements: Elements): void {
  dimensions.forEach(dimension => {
    const selected = state.selected.get(dimension.taxonomy);
    dimension.inputs.forEach(input => {
      input.checked = selected?.has(input.value) ?? false;
    });
  });

  elements.search.forEach(input => { input.value = state.search; });

  if (elements.sort !== null) elements.sort.value = state.sort;
}


// ---- Rendering ----

/**
 * A product matches when its text contains every word of the search term and
 * it carries one of the selected terms of every dimension. An empty selection
 * means "no restriction" -- so it is OR within a dimension and AND across them.
 *
 * `skip` leaves one dimension out, which is what the per-option counts need:
 * they show how many products an option would still yield given the *other*
 * dimensions. The search is never skipped: it narrows the counts just like the
 * grid.
 */
function matches(product: Product, state: State, skip?: string): boolean {
  for (const token of state.tokens) {
    if (!product.search.includes(token)) return false;
  }

  for (const [taxonomy, selected] of state.selected) {
    if (taxonomy === skip)  continue;
    if (selected.size === 0) continue;

    const term = product.terms.get(taxonomy);
    if (term === undefined || !selected.has(term)) return false;
  }
  return true;
}

function compare(sort: string, a: Product, b: Product): number {
  if (sort === 'price-asc')  return a.price - b.price;
  if (sort === 'price-desc') return b.price - a.price;
  if (sort === 'name')       return a.name.localeCompare(b.name, 'de');
  return a.order - b.order;
}

function render(
  state      : State,
  dimensions : Dimension[],
  products   : Product[],
  elements   : Elements,
  pageSize   : number
): void {
  const hits   = products.filter(product => matches(product, state));
  const misses = products.filter(product => !matches(product, state));

  hits.sort((a, b) => compare(state.sort, a, b));

  // Reorder in one pass: appending a node that is already a child moves it.
  const fragment = document.createDocumentFragment();
  hits  .forEach(product => fragment.appendChild(product.el));
  misses.forEach(product => fragment.appendChild(product.el));
  elements.grid.appendChild(fragment);

  const visible = Math.min(state.shown, hits.length);

  hits  .forEach((product, index) => toggle(product.el, index < visible));
  misses.forEach(product          => toggle(product.el, false));

  renderCounts(state, dimensions, products);
  renderChips(state, dimensions, elements);

  if (elements.result !== null) {
    elements.result.textContent = `${hits.length} von ${products.length} Produkten`;
  }

  // Drawer: Zaehler am Filter-Knopf, Trefferzahl auf dem Bestaetigen-Knopf.
  const active = countActive(state);

  if (elements.badge !== null) {
    elements.badge.textContent = String(active);
    setHidden(elements.badge, active === 0);
  }
  if (elements.apply !== null) {
    elements.apply.textContent = `${hits.length} Ergebnisse anzeigen`;
  }

  setHidden(elements.empty, hits.length > 0);
  setHidden(elements.more , hits.length <= visible);

  if (state.shown !== visible && hits.length > 0) state.shown = Math.max(visible, pageSize);
}

function countActive(state: State): number {
  let total = 0;
  state.selected.forEach(selected => { total += selected.size; });
  return total;
}

function toggle(el: HTMLElement, visible: boolean): void {
  if (visible) el.removeAttribute('hidden');
  else         el.setAttribute('hidden', '');
}

function setHidden(el: HTMLElement | null, hidden: boolean): void {
  if (el === null) return;
  if (hidden) el.setAttribute('hidden', '');
  else        el.removeAttribute('hidden');
}

/** Live counts: how many products each option would still yield. */
function renderCounts(state: State, dimensions: Dimension[], products: Product[]): void {
  dimensions.forEach(dimension => {
    const others = products.filter(product => matches(product, state, dimension.taxonomy));

    dimension.counts.forEach((el, slug) => {
      const count = others.filter(product => product.terms.get(dimension.taxonomy) === slug).length;
      el.textContent = String(count);

      dimension.items.get(slug)?.classList.toggle('is-empty', count === 0);
    });
  });
}

function renderChips(state: State, dimensions: Dimension[], elements: Elements): void {
  const chips = elements.chips;
  if (chips === null) return;

  chips.querySelectorAll('.c-product-toolbar__chip').forEach(chip => chip.remove());

  let total = 0;

  // Das Suchfeld steht im Seitenkopf, weit weg vom Raster. Als Chip ist der
  // Begriff dort sichtbar, wo die Trefferzahl steht -- und mit einem Klick weg.
  if (state.search !== '') {
    total += 1;
    chips.insertBefore(buildSearchChip(state, elements), elements.reset);
  }

  dimensions.forEach(dimension => {
    const selected = state.selected.get(dimension.taxonomy);
    if (selected === undefined) return;

    selected.forEach(slug => {
      total += 1;
      chips.insertBefore(buildChip(dimension, slug), elements.reset);
    });
  });

  setHidden(chips, total === 0);
}

function buildChip(dimension: Dimension, slug: string): HTMLElement {
  const label = dimension.labels.get(slug) ?? slug;

  // Round-trip through the checkbox so state, control and URL stay in sync.
  return assembleChip(`is-${dimension.taxonomy}`, label, `Filter ${label} entfernen`, () => {
    const input = dimension.inputs.find(candidate => candidate.value === slug);
    if (input === undefined) return;

    input.checked = false;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function buildSearchChip(state: State, elements: Elements): HTMLElement {
  // Wie oben ueber das Bedienelement: das `input`-Ereignis raeumt Zustand,
  // Feld und URL auf.
  return assembleChip('is-search', `Suche: ${state.search}`, 'Suche zurücksetzen', () => {
    const input = elements.search[0];
    if (input === undefined) return;

    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

function assembleChip(
  modifier : string,
  text     : string,
  title    : string,
  onRemove : () => void
): HTMLElement {
  const chip   = document.createElement('span');
  const label  = document.createElement('span');
  const remove = document.createElement('button');

  chip.className    = `c-product-toolbar__chip ${modifier}`;
  label.textContent = text;

  remove.type        = 'button';
  remove.className   = 'c-product-toolbar__chip-remove';
  remove.textContent = '×';
  remove.setAttribute('aria-label', title);

  remove.addEventListener('click', onRemove);

  chip.appendChild(label);
  chip.appendChild(remove);
  return chip;
}


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
